import React from 'react';
import PropTypes from 'prop-types';
import AutosuggestHighlightMatch from 'autosuggest-highlight/match';
import AutosuggestHighlightParse from 'autosuggest-highlight/parse';
import Autosuggest from 'react-autosuggest';
import classNames from 'classnames';
import { Scrollbar } from 'components';
import SearchListIcon from 'components/SvgIcons/ComplexIcons/search-list.svg';
import { searchTargets } from 'config/data-lists/searchPropsForAutocomplete';
import './autosuggestion-field.scss';

export class AutocompleteSimpleField extends React.PureComponent {
  static propTypes = {
    list: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        organizationName: PropTypes.string,
        value: PropTypes.string,
      }),
    ),
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onChangeDelay: PropTypes.number,
  };

  static defaultProps = {
    onChange: () => {},
    onFocus: () => {},
    onBlur: () => {},
    onChangeDelay: 0,
  };

  state = {
    value: '',
    suggestions: [],
    notFound: false,
    searchTarget: null,
  };

  timerId;

  componentDidMount() {
    this.setState({ value: this.props.value || '' });
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (prevProps.list !== this.props.list) {
      this.setState({ suggestions: this.getSuggestions(this.state.value) });
    }

    if (prevProps.value !== this.props.value) {
      this.setState({ value: this.props.value });
    }
  }

  getSuggestions = (value) => {
    const inputValue = value.trim().toLowerCase();
    const result = this.props.noFilter
      ? this.props.list
      : this.props.list.filter((listItem) => {
          const searchTarget = listItem[this.getPropperty()];
          return (
            searchTarget.toLowerCase().slice(0, inputValue.length) ===
            inputValue
          );
        });

    return inputValue.length === 0 ? [] : result;
  };

  getSuggestionValue = (suggestion) => suggestion[this.getPropperty()];

  onSuggestionsFetchRequested = ({ value }) => {
    this.setState({
      suggestions: this.getSuggestions(value),
    });
  };

  onSuggestionsClearRequested = () => {
    this.setState({
      suggestions: [],
    });
  };

  renderInputComponent = (inputProps) => {
    const {
      isPatientForm,
      label,
      icon: Icon,
      meta,
      required,
      activeSearch,
    } = this.props;
    const { disabled } = inputProps;
    const hasError = meta?.touched && meta?.error;

    return isPatientForm ? (
      <div
        className={classNames('form-group', 'patient-form-group', {
          _disabled: disabled,
        })}
      >
        <label className="patient-form-label">
          {required && <span className="required">*</span>}
          {label}
        </label>
        <input
          {...inputProps}
          type="text"
          className={`patient-form-control ${hasError ? 'error' : ''}`}
        />
      </div>
    ) : (
      <div className={classNames('input-component', { _disabled: disabled })}>
        {label && <span className="input-component__label">{label}</span>}
        <input {...inputProps} type="text" />
        {Icon && (
          <Icon
            style={activeSearch ? { fill: '#C83133' } : {}}
            className="search-component__search-icon"
          />
        )}
      </div>
    );
  };

  getPropperty = () => {
    for (let key in this.props.list[0]) {
      if (searchTargets.includes(key)) {
        return key;
      }
    }
  };

  renderSuggestion = (suggestion, { query }) => {
    const isString = typeof suggestion === 'string';
    let stringResult;

    if (isString) {
      stringResult = suggestion;
    } else {
      const addressTarget = suggestion.address || suggestion.locationAddress;
      stringResult = addressTarget
        ? `${suggestion[this.getPropperty()]}, ${addressTarget}`
        : suggestion[this.getPropperty()];
    }
    const matches = AutosuggestHighlightMatch(stringResult, query);
    const parts = AutosuggestHighlightParse(stringResult, matches);

    return (
      <div className="autosuggestion-field__suggestion">
        {parts.map((part, index) => (
          <span
            className={classNames({
              'react-autosuggest__suggestion-match': part.highlight,
            })}
            key={index}
          >
            {part.text}
          </span>
        ))}
      </div>
    );
  };

  renderSuggestionsContainer = ({ containerProps, children }) => {
    const height = this.state.suggestions.length * 32;

    if (children !== null) {
      return (
        <div
          {...containerProps}
          style={{ height: `${height}px` }}
          className="autosuggestion-field__suggestions-container drop-list"
        >
          <Scrollbar>{children}</Scrollbar>
        </div>
      );
    }

    if (children === null && this.props.notFound) {
      return (
        <div style={{ height: '170px' }} className="table__search">
          <SearchListIcon height="5.5rem" width="5.5rem" />
          <div className="montserrat color-gray medium-bold mt-7">
            {'No results found for your request'}
          </div>
        </div>
      );
    }
  };

  onSuggestionSelected = (e, { suggestion }) => {
    const isString = typeof suggestion === 'string';
    const suggestionTarget = isString
      ? suggestion
      : suggestion[this.getPropperty()];

    this.setState({ value: suggestionTarget });
    this.props.onChange(suggestionTarget);
    this.props.onRenderSelected && this.props.onRenderSelected(suggestion);
  };

  render() {
    const inputProps = {
      disabled: this.props.disabled,
      maxLength: this.props.maxLength,
      name: this.props.name,
      placeholder: this.props.placeholder || '',
      value: this.state.value,
      onChange: (e, { newValue }) => {
        this.setState({ value: newValue });
        if (this.timerId) {
          clearTimeout(this.timerId);
        }
        this.timerId = setTimeout(() => {
          this.props.onChange(newValue);
        }, this.props.onChangeDelay);
      },
      onFocus: this.props.onHandleFocusAndBlur,
      onBlur: this.props.onHandleFocusAndBlur,
    };

    return (
      <div className="autosuggestion-field">
        <Autosuggest
          suggestions={this.state.suggestions}
          onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
          onSuggestionsClearRequested={this.onSuggestionsClearRequested}
          getSuggestionValue={this.getSuggestionValue}
          renderSuggestion={this.renderSuggestion}
          renderSuggestionsContainer={this.renderSuggestionsContainer}
          inputProps={inputProps}
          renderInputComponent={this.renderInputComponent}
          onSuggestionSelected={this.onSuggestionSelected}
        />
      </div>
    );
  }
}
